import { useEffect, useId, useState, type FormEvent, type ReactNode } from "react";
import { X } from "lucide-react";
import { Button } from "../ui";
import { useApp } from "../../context/AppContext";
import { api } from "../../lib/api";
import type { Asset, FinancePlan } from "../../types";

const today = () => new Date().toISOString().slice(0, 10);
const moneyInput = (amountMinor: number) => (amountMinor / 100).toFixed(2);

function FinanceDialog({
  title,
  description,
  onClose,
  children,
  footer
}: {
  title: string;
  description: string;
  onClose: () => void;
  children: ReactNode;
  footer: ReactNode;
}) {
  const titleId = useId();
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div className="modal-backdrop finance-modal-backdrop" onMouseDown={onClose}>
      <dialog className="modal finance-editor-modal" open aria-labelledby={titleId} onMouseDown={(event) => event.stopPropagation()}>
        <header className="modal__header">
          <div>
            <h2 id={titleId}>{title}</h2>
            <p>{description}</p>
          </div>
          <button className="modal__close" type="button" onClick={onClose} aria-label="关闭">
            <X size={17} />
          </button>
        </header>
        {children}
        <footer className="modal__footer">{footer}</footer>
      </dialog>
    </div>
  );
}

export function AssetEditor({ asset, onClose }: { asset: Asset | null; onClose: () => void }) {
  const { mutate } = useApp();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: asset?.name ?? "",
    assetType: asset?.asset_type ?? "BANK",
    amount: asset ? moneyInput(asset.amount_minor) : "",
    currency: asset?.currency ?? "CNY",
    asOfDate: asset?.as_of_date ?? today(),
    note: asset?.note ?? ""
  });

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    const action = asset
      ? () => api.update("assets", asset.id, form)
      : () => api.create("assets", form);
    const result = await mutate(action, "");
    setSaving(false);
    if (result) onClose();
  };

  return (
    <form onSubmit={submit}>
      <FinanceDialog
        title={asset ? "更新财务现状" : "新增财务现状"}
        description="这里记录你现在拥有多少，不需要补录交易过程。"
        onClose={onClose}
        footer={
          <>
            <Button type="button" variant="ghost" onClick={onClose}>取消</Button>
            <Button type="submit" disabled={saving}>{saving ? "保存中…" : "保存现状"}</Button>
          </>
        }
      >
        <div className="modal__body form-grid form-grid--two">
          <label className="field field--full">
            <span>名称 <b>*</b></span>
            <input autoFocus required maxLength={100} placeholder="例如：保险库资金、招商银行储蓄" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          </label>
          <label className="field">
            <span>资金类型</span>
            <select value={form.assetType} onChange={(event) => setForm({ ...form, assetType: event.target.value as Asset["asset_type"] })}>
              <option value="CASH">现金</option><option value="BANK">银行存款</option><option value="INVESTMENT">投资</option><option value="RECEIVABLE">应收款</option><option value="FIXED">固定资产</option><option value="OTHER">其他</option>
            </select>
          </label>
          <label className="field">
            <span>盘点日期</span>
            <input type="date" required value={form.asOfDate} onChange={(event) => setForm({ ...form, asOfDate: event.target.value })} />
          </label>
          <label className="field">
            <span>当前金额 <b>*</b></span>
            <input type="number" min="0" step="0.01" inputMode="decimal" required placeholder="3000" value={form.amount} onChange={(event) => setForm({ ...form, amount: event.target.value })} />
          </label>
          <label className="field">
            <span>币种</span>
            <select value={form.currency} onChange={(event) => setForm({ ...form, currency: event.target.value })}>
              <option value="CNY">人民币 CNY</option><option value="USD">美元 USD</option><option value="EUR">欧元 EUR</option><option value="HKD">港币 HKD</option><option value="JPY">日元 JPY</option>
            </select>
          </label>
          <label className="field field--full">
            <span>备注</span>
            <textarea rows={3} maxLength={300} placeholder="说明这笔资金的用途或限制" value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} />
          </label>
        </div>
      </FinanceDialog>
    </form>
  );
}

export function FinancePlanEditor({ plan, onClose }: { plan: FinancePlan | null; onClose: () => void }) {
  const { mutate } = useApp();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: plan?.title ?? "",
    planType: plan?.plan_type ?? "SAVING",
    amount: plan ? moneyInput(plan.amount_minor) : "",
    currency: plan?.currency ?? "CNY",
    cadence: plan?.cadence ?? "ONE_TIME",
    targetDate: plan?.target_date ?? "",
    note: plan?.note ?? "",
    status: plan?.status ?? "ACTIVE"
  });

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    const action = plan
      ? () => api.update("financial-plans", plan.id, form)
      : () => api.create("financial-plans", form);
    const result = await mutate(action, "");
    setSaving(false);
    if (result) onClose();
  };

  return (
    <form onSubmit={submit}>
      <FinanceDialog
        title={plan ? "编辑财务计划" : "新增财务计划"}
        description="计划表达未来准备怎么用钱，不会被当成已发生的流水。"
        onClose={onClose}
        footer={
          <>
            <Button type="button" variant="ghost" onClick={onClose}>取消</Button>
            <Button type="submit" disabled={saving}>{saving ? "保存中…" : "保存计划"}</Button>
          </>
        }
      >
        <div className="modal__body form-grid form-grid--two">
          <label className="field field--full">
            <span>计划名称 <b>*</b></span>
            <input autoFocus required maxLength={120} placeholder="例如：年底前存下 2 万元" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} />
          </label>
          <label className="field">
            <span>计划类型</span>
            <select value={form.planType} onChange={(event) => setForm({ ...form, planType: event.target.value as FinancePlan["plan_type"] })}>
              <option value="SAVING">存钱计划</option><option value="SPENDING">开销计划</option>
            </select>
          </label>
          <label className="field">
            <span>状态</span>
            <select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value as FinancePlan["status"] })}>
              <option value="ACTIVE">进行中</option><option value="PAUSED">暂停</option><option value="COMPLETED">已完成</option>
            </select>
          </label>
          <label className="field">
            <span>{form.planType === "SAVING" ? "目标金额" : "计划上限"} <b>*</b></span>
            <input type="number" min="0" step="0.01" inputMode="decimal" required placeholder="5000" value={form.amount} onChange={(event) => setForm({ ...form, amount: event.target.value })} />
          </label>
          <label className="field">
            <span>币种</span>
            <select value={form.currency} onChange={(event) => setForm({ ...form, currency: event.target.value as FinancePlan["currency"] })}>
              <option value="CNY">人民币 CNY</option><option value="USD">美元 USD</option><option value="EUR">欧元 EUR</option><option value="HKD">港币 HKD</option><option value="JPY">日元 JPY</option>
            </select>
          </label>
          <label className="field">
            <span>频率</span>
            <select value={form.cadence} onChange={(event) => setForm({ ...form, cadence: event.target.value as FinancePlan["cadence"] })}>
              <option value="ONE_TIME">一次性计划</option><option value="MONTHLY">每月</option><option value="YEARLY">每年</option>
            </select>
          </label>
          <label className="field">
            <span>目标日期（可选）</span>
            <input type="date" value={form.targetDate} onChange={(event) => setForm({ ...form, targetDate: event.target.value })} />
          </label>
          <label className="field field--full">
            <span>计划说明</span>
            <textarea rows={4} maxLength={500} placeholder="写下用途、边界或执行方法" value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} />
          </label>
        </div>
      </FinanceDialog>
    </form>
  );
}
